/**
 * SolarConfiguratorFunnel State Machine
 * 
 * Manages the multi-step funnel progression:
 * 1. Tracks current step index and FunnelStep object
 * 2. Updates funnel data as user progresses
 * 3. Persists funnel data to localStorage
 * 4. Supports forward navigation, step jumping, and reset
 */

import { FUNNEL_STEP_IDS } from "../constants/funnelSteps";
import type { EnterDetailsStepData } from "./Funnel/EnterDetailsStep";
import type { SolarConfiguratorStepData } from "./Funnel/SolarConfiguratorStep";

export type FunnelState = {
  currentStepIndex: number;
  currentFunnelStep: FunnelStep;
  funnelData: FunnelData;
  isAtConfiguratorStep: boolean;
};

export type FunnelAction =
  | {
      type: "INITIALIZE";
      payload: {
        stepIndex: number;
        funnelStep: FunnelStep;
        funnelData: FunnelData;
      };
    }
  | {
      type: "UPDATE_STEP_DATA";
      payload: {
        stepId: string;
        stepData: FunnelStepData;
      };
    }
  | {
      type: "SUBMIT_STEP_DATA";
      payload: {
        stepId: string;
        stepData: FunnelStepData;
        allFunnelSteps: FunnelStep[];
        configuratorStep: FunnelStep;
      };
    }
  | {
      type: "GO_TO_STEP";
      payload: {
        stepIndex: number;
        allFunnelSteps: FunnelStep[];
      };
    }
  | {
      type: "NEXT_STEP";
      payload: {
        allFunnelSteps: FunnelStep[];
        configuratorStep: FunnelStep;
      };
    }
  | {
      type: "RESET";
      payload: {
        firstStep: FunnelStep;
      };
    };

export const initialFunnelState = (
  stepIndex: number,
  currentFunnelStep: FunnelStep,
  funnelData: FunnelData
): FunnelState => ({
  currentStepIndex: stepIndex,
  currentFunnelStep,
  funnelData,
  isAtConfiguratorStep: currentFunnelStep.id === FUNNEL_STEP_IDS.SOLAR_CONFIGURATOR,
});

/**
 * Computes the next funnel data when a step is submitted.
 *
 * Besides storing the step's own data, this derives cross-step state that used to
 * live in per-step lifecycle hooks: submitting `details-input` keeps the
 * configurator's `powerConsumption` in sync with the selected consumption plan —
 * but only when the user actually picked a *different* plan than last time.
 *
 * This preserves a value the user manually entered in the configurator: walking
 * back through the funnel and re-submitting `details-input` without changing the
 * plan must not overwrite the manual value. Changing the plan, however, replaces it.
 *
 * Pure and exported so the provider can persist the exact same result to
 * localStorage after dispatching.
 */
export const computeSubmittedFunnelData = (
  funnelData: FunnelData,
  stepId: string,
  stepData: FunnelStepData
): FunnelData => {
  const nextFunnelData: FunnelData = {
    ...funnelData,
    [stepId]: stepData,
  };

  if (stepId === FUNNEL_STEP_IDS.DETAILS_INPUT) {
    const previousPlanValue = (funnelData[FUNNEL_STEP_IDS.DETAILS_INPUT] as
      | EnterDetailsStepData
      | undefined)?.selectedPowerConsumptionEstimate?.value;
    const newPlanValue = (stepData as EnterDetailsStepData)
      .selectedPowerConsumptionEstimate?.value;
    const configurator = nextFunnelData[FUNNEL_STEP_IDS.SOLAR_CONFIGURATOR] as
      | SolarConfiguratorStepData
      | undefined;

    // Only override the (possibly manually edited) configurator value when the
    // selected plan actually changed compared to the previously submitted plan.
    if (configurator && newPlanValue !== previousPlanValue) {
      nextFunnelData[FUNNEL_STEP_IDS.SOLAR_CONFIGURATOR] = {
        ...configurator,
        powerConsumption: newPlanValue,
      };
    }
  }

  return nextFunnelData;
};

/**
 * Reducer for SolarConfiguratorFunnel state management
 * 
 * State transitions:
 * - INITIALIZE: Load funnel state from LocalStorage
 * - UPDATE_STEP_DATA: Update current step data (without navigation)
 * - SUBMIT_STEP_DATA: Update data and proceed to next step
 * - GO_TO_STEP: Jump to specific step (stepper click)
 * - NEXT_STEP: Navigate to next step
 * - RESET: Return to first step and clear data
 */
export const funnelReducer = (
  state: FunnelState,
  action: FunnelAction
): FunnelState => {
  switch (action.type) {
    case "INITIALIZE": {
      const { stepIndex, funnelStep, funnelData } = action.payload;
      return {
        currentStepIndex: stepIndex,
        currentFunnelStep: funnelStep,
        funnelData,
        isAtConfiguratorStep: funnelStep.id === FUNNEL_STEP_IDS.SOLAR_CONFIGURATOR,
      };
    }

    case "UPDATE_STEP_DATA": {
      const { stepId, stepData } = action.payload;
      return {
        ...state,
        funnelData: {
          ...state.funnelData,
          [stepId]: stepData,
        },
      };
    }

    case "SUBMIT_STEP_DATA": {
      const { stepId, stepData, allFunnelSteps, configuratorStep } = action.payload;
      const newFunnelData = computeSubmittedFunnelData(state.funnelData, stepId, stepData);

      const nextStepIndex = state.currentStepIndex + 1;
      const nextFunnelStep = allFunnelSteps[nextStepIndex] || configuratorStep;

      return {
        currentStepIndex: nextStepIndex,
        currentFunnelStep: nextFunnelStep,
        funnelData: newFunnelData,
        isAtConfiguratorStep: nextFunnelStep.id === FUNNEL_STEP_IDS.SOLAR_CONFIGURATOR,
      };
    }

    case "GO_TO_STEP": {
      const { stepIndex, allFunnelSteps } = action.payload;
      
      if (stepIndex < 0 || stepIndex >= allFunnelSteps.length) {
        return state;
      }

      return {
        ...state,
        currentStepIndex: stepIndex,
        currentFunnelStep: allFunnelSteps[stepIndex],
        isAtConfiguratorStep: allFunnelSteps[stepIndex].id === FUNNEL_STEP_IDS.SOLAR_CONFIGURATOR,
      };
    }

    case "NEXT_STEP": {
      const { allFunnelSteps, configuratorStep } = action.payload;
      const nextStepIndex = state.currentStepIndex + 1;
      const nextFunnelStep = allFunnelSteps[nextStepIndex] || configuratorStep;

      return {
        ...state,
        currentStepIndex: nextStepIndex,
        currentFunnelStep: nextFunnelStep,
        isAtConfiguratorStep: nextFunnelStep.id === FUNNEL_STEP_IDS.SOLAR_CONFIGURATOR,
      };
    }

    case "RESET": {
      const { firstStep } = action.payload;
      return {
        currentStepIndex: 0,
        currentFunnelStep: firstStep,
        funnelData: {},
        isAtConfiguratorStep: false,
      };
    }

    default:
      return state;
  }
};